/**
 * Full router for the client side
 */

import Vue from "vue";
import Router from "vue-router";

// Loaded on startup
import YearlyHome from "../components/Appointment/Yearly/Home.vue";
import YearlyVue from "../components/Appointment/Yearly/Year.vue";
import LoginHome from "../components/Authentication/Login/Home.vue";
import MonthlyHome from "../components/Appointment/Monthly/Home.vue";
import DailyHome from "../components/Appointment/Daily/Home.vue";
import AddHome from "../components/Appointment/AddAppointment/Home.vue";
import AppointmentDetailHome from "../components/Appointment/AppointmentDetails/Home.vue";
import NotFoundHome from "../components/NotFound/Home";

// Lazy loading modules
const OthersHome = () => import("../components/Others/Home.vue");
const ChatHome = () => import("../components/Chat/Home");
const ChatDashboardHome = () => import("../components/Chat/Dashboard/Home");
const EmailDashboardHome = () => import("../components/Portalemail/Dashboard/Home");
const MailDetail = () => import("../components/Portalemail/MailDetail/MailDetail");
const ComposeEmailHome = () => import("../components/Portalemail/ComposeEmail/Home");
const AddClientHome = () => import("../components/Clients/AddClient/Home");
const AddPersonHome = () => import("../components/Person/AddPerson/Home");
const AddGroupHome = () => import("../components/Group/AddGroup/Home");
const AddEventHome = () => import("../components/Event/AddEvent/Home");
const PhoneHome = () => import("../components/Phone/Home");
const ForgotPasswordEmail = () => import("../components/ForgotPassword/Email");
const ForgotPasswordQuestions = () =>
  import("../components/ForgotPassword/Questions");
const ForgotPasswordReset = () =>
  import("../components/ForgotPassword/ResetPassword");

// Store
import { store } from "../store/store";

// Chrome 1 - 68
const isChrome =
  /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);

// Use router
Vue.use(Router);

/**
 * Checking if user is authorized
 * @param {Object} to
 * @param {Object} from
 * @param {Object} next
 */
let requiredAuth = async (to, from, next, userRoleCheck = false) => {

  // Step 1: Set the previous path
  store.commit("setPreviousRoute", from.fullPath);

  // Step 2: Set the local from local storage / session storage
  store.commit("setLocal");

  // Step 3: Perform a session check if session check has not been done
  if (store.getters.getUserRole === 0) {
    await store.dispatch("setExpiryAndPermission");
  }

  // Step 3: If the session is invalid, then route the user to the login page
  if (!store.getters.checkSession) {
    store.commit("setNextRoute", to.fullPath);
    return next("/login");
  }

  /**
   * Check email permission
   */
  if((store.getters.getUserRole === 24) &&
    (to.fullPath.includes("/emails/"))) {

    if(store.getters.getEmailTabPermission[store.getters.getSelectedProviderID] &&
      ('denied' === store.getters.getEmailTabPermission[store.getters.getSelectedProviderID])) {
      store.dispatch("globalMessagePopup",
        'Sorry! You don\'t have sufficient permission to access the email functionality.');
      return next('/login' === from.fullPath ? '/daily' : from.fullPath);
    }

    if(!store.getters.getEmailTabPermission[store.getters.getSelectedProviderID]) {
      let permRes = await checkEmailPermission();
      if (permRes.error) {
        store.commit("setEmailTabPermission", "denied");
        store.dispatch("globalMessagePopup", permRes.error);
        return next('/login' === from.fullPath ? '/daily' : from.fullPath);
      } else {
        store.commit("setEmailTabPermission", "allowed");
      }
    }
  }

  // Step 4: When user role check is not required allow user to pass
  if (!userRoleCheck) {
    return next();
  }

  // Step 5: When there is an user role check
  if (
    (store.getters.getUserRole === 8 ||
      store.getters.getUserRole === 7 ||
      store.getters.getUserRole === 11 ||
      store.getters.getUserRole === 24) &&
    (store.getters.getUserType === 1 ||
      store.getters.getUserType === 2 ||
      store.getters.getUserType === 4)
  ) {
    return next();
  }
  return next("/");
}

/**
 * Function to check email permission for app user
 *
 * @returns {Promise<void>}
 */
async function checkEmailPermission() {
  return await store.dispatch(
    "makeAuthRequest",
    {
      url: `email/checkPermission/${store.getters.getSelectedProviderID}`,
      method: "GET",
      chat: true
    },
    {
      root: true
    }
  );
}

export default new Router({
  routes: [
    /**
     * Original home page was yearly
     * Now redirecting to daily
     */
    {
      path: "/",
      name: "Dashbord",
      beforeEnter(to, from, next) {
        next("/daily");
      }
    },

    /**
     * Yearly page
     * Shows the yearly calendar with appointment dates
     * Auth is required for this route
     */
    {
      path: "/year/:year",
      component: YearlyHome,
      children: [
        {
          path: "",
          name: "ShowYear",
          component: YearlyVue
        }
      ],
      beforeEnter: requiredAuth
    },

    /**
     * Monthly page
     * Shows the monthly calendar with appointment dates
     * Auth is required for this route
     */
    {
      path: "/monthly",
      name: "Monthly",
      component: MonthlyHome,
      beforeEnter: requiredAuth
    },

    /**
     * Daily page
     * Shows the daily page with all the appointment list of that day
     * Auth is required
     */
    {
      path: "/daily",
      name: "Daily",
      component: DailyHome,
      beforeEnter: requiredAuth
    },

    /**
     * Add appointment page
     * Shows the add appointment page
     * Special auth is required for this page
     */
    {
      path: "/add",
      name: "Add",
      component: AddHome,
      beforeEnter: (to, from, next) => requiredAuth(to, from, next, true)
    },

    /**
     * Appointment details page
     * Edit appointment, delete appointment and cancel appointment happens on this page
     * Auth is required for this page
     */
    {
      path: "/appointment_detail/:id",
      name: "AppointmentDetail",
      component: AppointmentDetailHome,
      beforeEnter: requiredAuth
    },

    /**
     * Contacts page
     * Shows the contacts page of the user
     * Special auth is required for this page
     */
    {
      path: "/contacts",
      name: "Contacts",
      component: OthersHome,
      beforeEnter: (to, from, next) => requiredAuth(to, from, next, true)
    },

    /**
     * Individual messages page
     * Shows messages between a specific user
     * Auth is required for this page
     */
    {
      path: "/messenger",
      name: "Chat",
      component: ChatHome,
      beforeEnter: requiredAuth
    },

    /**
     * Messenger for a particular id
     * Shows messages between a specific user (for special redirects from external links)
     * Auth is required for this page
     */
    {
      path: "/messenger/:id",
      name: "ChatId",
      component: ChatHome,
      beforeEnter: requiredAuth
    },

    /**
     * Messenger for a particular group id
     * Shows messages for a particular group
     * Auth is required for this page
     */
    {
      path: "/messenger/group/:id",
      name: "GroupChatId",
      component: ChatHome,
      beforeEnter: requiredAuth
    },

    /**
     * Dash board for the pages
     * Shows all the chats history of the user
     * Auth is required for this page
     */
    {
      path: "/chat_dashboard",
      name: "Chat Dashboard",
      component: ChatDashboardHome,
      beforeEnter: requiredAuth
    },

    /**
     * Dash board for the pages
     * Shows all the email history of the user
     * Auth is required for this page
     */
    {
      path: "/compose-email",
      name: "ComposeEmail",
      component: ComposeEmailHome,
      beforeEnter: requiredAuth
    },

    /**
     * Dash board for the pages
     * Shows all the email history of the user
     * Auth is required for this page
     */
    {
      path: "/emails/:type",
      name: "EmailDashboard",
      component: EmailDashboardHome,
      beforeEnter: requiredAuth
    },

      /**
       * The email details page route.
       * Shows the mail details
       * Auth is required.
       */
      {
          path: "/emails/:type/:id",
          name: "EmailDetail",
          component: MailDetail,
          beforeEnter: requiredAuth
      },

    /**
     * Dash board for the pages
     * Shows all the email history of the user
     * Auth is required for this page
     */
    {
      path: "/folder/:type/:folderId",
      name: "FolderEmail",
      component: EmailDashboardHome,
      beforeEnter: requiredAuth
    },

    /**
     * Add client page
     * Special auth is required for this page
     */
    {
      path: "/add_client",
      name: "AddClient",
      component: AddClientHome,
      beforeEnter: (to, from, next) => requiredAuth(to, from, next, true)
    },

    /**
     * Add person page (Currently no longer used)
     * For adding other_event
     * Special auth is required for this page
     */
    {
      path: "/add_person",
      name: "AddPerson",
      component: AddPersonHome,
      beforeEnter: (to, from, next) => requiredAuth(to, from, next, true)
    },

    /**
     * Add event page (Currently no longer used)
     * For adding event_appointment
     * Special auth is required for this page
     */
    {
      path: "/add_event",
      name: "AddEvent",
      component: AddEventHome,
      beforeEnter: (to, from, next) => requiredAuth(to, from, next, true)
    },

    /**
     * Add group pahe (Currently no longer used)
     * For adding groups
     * Special auth is required for this page
     */
    {
      path: "/add_group",
      name: "AddGroup",
      component: AddGroupHome,
      beforeEnter: (to, from, next) => requiredAuth(to, from, next, true)
    },

    /**
     * Phone page for calling (Currently no longer used)
     * For adding phones
     * Special auth is required for this page
     */
    {
      path: "/phone",
      name: "Phone",
      component: PhoneHome,
      beforeEnter: (to, from, next) => requiredAuth(to, from, next, true)
    },

    /**
     * Login page
     * Reverse auth is required
     */
    {
      path: "/login",
      name: "Login",
      component: LoginHome,
      beforeEnter(to, from, next) {
        store.commit("setLocal");
        if (store.getters.checkSession) {
          next("/");
        } else {
          next();
        }
      }
    },

    /**
     * Forgot password page
     * Reverse auth is required
     */
    {
      path: "/forgot_email",
      name: "ForgotEmail",
      component: ForgotPasswordEmail,
      beforeEnter(to, from, next) {
        store.commit("setLocal");
        if (store.getters.checkSession) {
          next("/");
        } else {
          next();
        }
      }
    },

    /**
     * Forgot questions page
     * Reverse auth is required for this page
     */
    {
      path: "/forgot_questions",
      name: "ForgotQuestions",
      component: ForgotPasswordQuestions,
      beforeEnter(to, from, next) {
        store.commit("setLocal");
        if (store.getters.checkSession) {
          next("/");
        } else if (store.getters["forgotPassword/getUserId"] === 0) {
          next("/forgot_email");
        } else {
          next();
        }
      }
    },

    /**
     * Reset password page
     */
    {
      path: "/forgot_reset/:token",
      name: "ForgotReset",
      component: ForgotPasswordReset
    },

    /**
     * Default 404 page
     */
    {
      path: "*",
      name: "404",
      component: NotFoundHome
    }
  ],
  /**
   * History mode for beautified routes
   */
  mode: "history",
  /**
   *
   * Setting scroll behaviour for monthly view page.
   * Scroll behaviour works differently in Chrome and on other browsers
   *
   */
  scrollBehavior(to, from, savedPosition) {
    // Step 1: When the current route is not monthly
    if (to.fullPath !== "/monthly" && savedPosition) {
      // Step 1.1: When there is a saved position, return the saved position
      return savedPosition;
    } else if (to.fullPath !== "/monthly") {
      // Step 1.1: When no saved postion, return 0, 0 position
      return { x: 0, y: 0 };
    }

    // Step 2: Get the co-ordinates of the stored months
    const coordinatesMonth = store.getters.getCoordinates;

    // Step 3: Get the selected month
    const selectedMonth = store.getters.getSelectedMonth;

    // Step 4: Check if is chrome
    if (isChrome) {
      // Step 4.1: Return the co-ordinates of that month
      return coordinatesMonth[selectedMonth];
    }

    // Step 5: If not chrome scroll to the position of the month
    window.scroll({
      left: 0,
      top: coordinatesMonth[selectedMonth].y - coordinatesMonth[0].y,
      behavior: "smooth"
    });
  }
});
